/**
 * Molecule – ReportForm
 * HR daily-report submission form.
 *
 * Props:
 *  - onSuccess(report)  – called after successful API submit
 *  - existingReport     – if present the form shows as read-only (already submitted today)
 *  - compact            – renders a slightly tighter layout (used inside PunchOutGuardModal)
 */
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { MdAdd, MdSend, MdCheckCircle } from 'react-icons/md';
import TaskTag from '../atoms/TaskTag';
import dailyReportService from '../../../services/dailyReportService';

const FIELD = {
  label: 'text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1.5',
  textarea:
    'w-full px-3 py-2.5 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent-green/40 focus:border-accent-green text-sm text-text-primary placeholder-text-tertiary resize-none transition-all duration-150',
  input:
    'flex-1 px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-accent-green/40 focus:border-accent-green text-sm text-text-primary placeholder-text-tertiary transition-all duration-150'
};

const ReportForm = ({ onSuccess, existingReport, compact = false }) => {
  const [form, setForm] = useState({
    workSummary: '',
    issuesFaced: '',
    planForTomorrow: ''
  });
  const [tasks, setTasks] = useState([]);
  const [taskInput, setTaskInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ── Read-only view when already submitted ──────────────────────────────────
  if (existingReport) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2 p-3 rounded-lg bg-status-success-bg border border-status-success/30">
          <MdCheckCircle className="text-status-success flex-shrink-0" size={18} />
          <span className="text-sm font-medium text-status-success">
            Today's report has been submitted.
          </span>
        </div>

        <Section label="Work Summary">
          <p className="text-sm text-text-primary whitespace-pre-wrap">{existingReport.workSummary}</p>
        </Section>

        {existingReport.tasksCompleted?.length > 0 && (
          <Section label="Tasks Completed">
            <div className="flex flex-wrap gap-2">
              {existingReport.tasksCompleted.map((t, i) => (
                <TaskTag key={i} task={t} />
              ))}
            </div>
          </Section>
        )}

        {existingReport.issuesFaced && (
          <Section label="Issues Faced">
            <p className="text-sm text-text-primary whitespace-pre-wrap">{existingReport.issuesFaced}</p>
          </Section>
        )}

        {existingReport.planForTomorrow && (
          <Section label="Plan for Tomorrow">
            <p className="text-sm text-text-primary whitespace-pre-wrap">{existingReport.planForTomorrow}</p>
          </Section>
        )}

        {existingReport.chairmanFeedback && (
          <Section label="Chairman's Feedback">
            <div className="p-3 rounded-lg bg-status-warning-bg border border-status-warning/30">
              <p className="text-sm text-text-primary whitespace-pre-wrap">{existingReport.chairmanFeedback}</p>
            </div>
          </Section>
        )}
      </div>
    );
  }

  // ── Editable form ──────────────────────────────────────────────────────────
  const addTask = () => {
    const t = taskInput.trim();
    if (!t || tasks.includes(t) || tasks.length >= 50) return;
    setTasks((prev) => [...prev, t]);
    setTaskInput('');
  };

  const removeTask = (task) => setTasks((prev) => prev.filter((t) => t !== task));

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addTask(); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.workSummary.trim()) {
      toast.error('Work summary is required.');
      return;
    }
    try {
      setSubmitting(true);
      const res = await dailyReportService.createReport({
        workSummary: form.workSummary.trim(),
        tasksCompleted: tasks,
        issuesFaced: form.issuesFaced.trim(),
        planForTomorrow: form.planForTomorrow.trim()
      });
      if (res.success) {
        toast.success('Daily report submitted successfully!');
        onSuccess && onSuccess(res.data);
      }
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit report.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Work Summary */}
      <div>
        <label className={FIELD.label}>
          Work Summary <span className="text-status-error">*</span>
        </label>
        <textarea
          className={FIELD.textarea}
          rows={compact ? 3 : 5}
          maxLength={3000}
          placeholder="Describe what you worked on today…"
          value={form.workSummary}
          onChange={(e) => setForm((f) => ({ ...f, workSummary: e.target.value }))}
          required
        />
        <p className="text-right text-xs text-text-tertiary mt-0.5">{form.workSummary.length}/3000</p>
      </div>

      {/* Tasks Completed */}
      <div>
        <label className={FIELD.label}>Tasks Completed</label>
        <div className="flex gap-2">
          <input
            type="text"
            className={FIELD.input}
            placeholder="Type a task and press Enter or Add…"
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            onKeyDown={handleKeyDown}
            maxLength={300}
          />
          <button
            type="button"
            onClick={addTask}
            className="flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-lg bg-accent-green text-white text-sm font-medium hover:bg-accent-green-dark transition-colors"
          >
            <MdAdd size={16} /> Add
          </button>
        </div>
        {tasks.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {tasks.map((t, i) => (
              <TaskTag key={i} task={t} onRemove={removeTask} />
            ))}
          </div>
        )}
      </div>

      {/* Issues Faced */}
      <div>
        <label className={FIELD.label}>Issues Faced</label>
        <textarea
          className={FIELD.textarea}
          rows={compact ? 2 : 3}
          maxLength={3000}
          placeholder="Any blockers or challenges? (optional)"
          value={form.issuesFaced}
          onChange={(e) => setForm((f) => ({ ...f, issuesFaced: e.target.value }))}
        />
      </div>

      {/* Plan for Tomorrow */}
      <div>
        <label className={FIELD.label}>Plan for Tomorrow</label>
        <textarea
          className={FIELD.textarea}
          rows={compact ? 2 : 3}
          maxLength={3000}
          placeholder="What do you plan to do tomorrow? (optional)"
          value={form.planForTomorrow}
          onChange={(e) => setForm((f) => ({ ...f, planForTomorrow: e.target.value }))}
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={submitting}
        className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-accent-green text-white font-semibold text-sm hover:bg-accent-green-dark active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-150"
      >
        {submitting ? (
          <>
            <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            Submitting…
          </>
        ) : (
          <>
            <MdSend size={16} />
            Submit Daily Report
          </>
        )}
      </button>
    </form>
  );
};

// tiny helper used in read-only section
const Section = ({ label, children }) => (
  <div>
    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1.5">{label}</p>
    {children}
  </div>
);

export default ReportForm;
